/**
 * useApprove — sends an ERC-20 `approve` transaction to grant the
 * AsicExecutor permission to pull tokens. Uses max uint256 so users
 * only need to approve once per token.
 */

import { useState, useCallback, useEffect } from "react";
import { useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import type { Address } from "viem";
import { ERC20_ABI, ASIC_EXECUTOR_ADDRESS } from "@/lib/contracts";

const MAX_UINT256 = 2n ** 256n - 1n;

export type ApprovalState =
  | "idle"
  | "prompting"    // wallet dialog open
  | "pending"      // tx submitted, waiting for confirmation
  | "confirmed"    // receipt received, approval done
  | "error";

export function useApprove(tokenAddress: Address | undefined) {
  const [state, setState] = useState<ApprovalState>("idle");
  const [error, setError] = useState<string | null>(null);

  const { writeContractAsync, data: txHash, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
      query: {
        enabled: !!txHash,
      },
    });

  // Sync receipt status back to state
  useEffect(() => {
    if (isConfirmed && state === "pending") {
      setState("confirmed");
    }
  }, [isConfirmed, state]);

  const approve = useCallback(async () => {
    if (!tokenAddress) return;
    setState("prompting");
    setError(null);
    try {
      await writeContractAsync({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [ASIC_EXECUTOR_ADDRESS, MAX_UINT256],
      });
      setState("pending");
    } catch (err) {
      const msg = (err as Error).message;
      // User rejected in wallet — don't treat as a hard error
      if (msg.includes("User rejected") || msg.includes("user rejected")) {
        setState("idle");
      } else {
        setState("error");
        setError(msg);
      }
    }
  }, [tokenAddress, writeContractAsync]);

  const resetApproval = useCallback(() => {
    setState("idle");
    setError(null);
    reset();
  }, [reset]);

  return {
    state,
    error,
    txHash,
    isConfirming,
    approve,
    reset: resetApproval,
  };
}
